import { useEffect, useState } from 'react';
import { fetchSprints, fetchTicket } from '../../api/endpoints';
import type { Id, Sprint, TicketDetail } from '../../api/types';
import { TicketFormDialog } from '../../features/tickets/TicketFormDialog';
import { useTicketEditor } from '../../features/tickets/useTicketEditor';
import { fetchVersions, type Version, type VersionsResult } from '../../features/versions/versions-api';
import { useResource } from '../../hooks/useResource';
import { formatDateTime } from '../../lib/format';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import { StatusDropdown } from '../ticket/StatusDropdown';
import { TypeIcon } from '../ticket/TypeIcon';
import { ErrorNotice, StaleBanner } from '../ui/Notice';
import { SkeletonBar, Spinner } from '../ui/Skeleton';
import { CommentList } from './CommentList';
import { DetailFields } from './DetailFields';
import { GitlabCodePanel } from '../../features/gitlab/GitlabCodePanel';
import { LinkList } from './LinkList';
import { StatusHistory } from './StatusHistory';
import { SubtaskTable } from './SubtaskTable';

type ActivityTab = 'comments' | 'history';

// ⭐ Module-level constants, not `[]` inline. A fresh array literal in a render is a new identity
//   every time, which would defeat the memoisation in anything downstream that depends on it.
const NO_SPRINTS: Sprint[] = [];
const NO_VERSIONS: Version[] = [];

/**
 * 🔴 A PANEL, NOT A MODAL. On desktop it opens BESIDE the list and the list stays visible and usable
 * — that is the whole point of the three-pane shell in Or's screenshots, and a modal would destroy
 * it. On a phone the list is the primary view, so the detail takes the full screen with a back
 * affordance rather than becoming a 320px column of squeezed labels.
 */
export function TicketDetailPanel({
  ticketId,
  onClose,
  onOpenTicket,
  onStatusChanged,
  projectGitlabId,
}: {
  ticketId: Id;
  onClose: () => void;
  onOpenTicket: (id: Id) => void;
  onStatusChanged: () => void;
  /**
   * PH-62 — the ticket's project's `gitlab_project_id`, or null/absent.
   *
   * ⛔⛔ PASSED IN RATHER THAN READ FROM `useProjects()` HERE, and that is a correction of my own first
   *   attempt. Calling the hook made this panel REQUIRE ProjectsProvider — a context it had never
   *   needed — and seven existing tests went red with "useProjects must be used inside
   *   ProjectsProvider". ⭐ That was the component telling me I had widened its dependencies, not the
   *   tests being inconvenient: every screen that renders this panel would now have to sit inside a
   *   provider whether or not it cares about GitLab.
   * ⚠️ Absent ⇒ null ⇒ the GitLab section is ABSENT, which is exactly the correct rendering for a
   *   project with no link. So a caller that does not pass it loses a feature, never correctness.
   */
  projectGitlabId?: Id | null;
}) {
  const vocab = useVocabulary();
  const [tab, setTab] = useState<ActivityTab>('comments');
  const ticket = useResource<TicketDetail>((signal) => fetchTicket(ticketId, signal), [ticketId]);

  /**
   * PH-57 — editing from the panel.
   *
   * ⭐ THE EDITOR IS OWNED HERE rather than passed down from whichever screen opened the panel. The
   * panel is rendered by the backlog, the list AND the board; a prop would have to be threaded
   * through three call sites that otherwise share nothing, and each would need its own copy of the
   * sprint and release lists.
   *
   * ⚠️ `onSaved` reloads THIS ticket AND tells the list, because a saved edit can change what the
   * row behind the panel says (title, assignee, points) or move it out of the section entirely
   * (sprint). Reloading only the panel would leave the list showing the old row next to the new one.
   */
  const projectId = ticket.data?.project_id ?? null;
  const editor = useTicketEditor(projectId, () => {
    ticket.reload();
    onStatusChanged();
  });

  // ⭐ Fetched only while the form is open — a reader who never presses Edit pays for neither list.
  const sprintsResource = useResource<Sprint[]>(
    (signal) => fetchSprints(projectId as Id, signal),
    [projectId],
    { enabled: Boolean(projectId) && editor.open !== null },
  );
  const versionsResource = useResource<VersionsResult>(
    (signal) => fetchVersions(projectId as Id, signal),
    [projectId],
    { enabled: Boolean(projectId) && editor.open !== null },
  );
  const versions =
    versionsResource.data && versionsResource.data.kind !== 'endpoint-missing'
      ? versionsResource.data.versions
      : NO_VERSIONS;

  // Escape closes the panel — on desktop it is the fastest way back to the list, and on mobile it is
  // what a hardware/soft back gesture maps to.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const data = ticket.data;

  return (
    <aside
      aria-label="Ticket detail"
      className="flex h-full min-h-0 flex-col bg-[color:var(--color-surface)] lg:border-l lg:border-[color:var(--color-line)]"
    >
      <header className="flex items-center gap-2 border-b border-[color:var(--color-line)] px-3 py-2.5 sm:px-4">
        <button
          type="button"
          onClick={onClose}
          className="-ml-1 flex h-9 items-center gap-1.5 rounded px-2 text-sm font-medium text-[color:var(--color-ink-muted)] hover:bg-black/5 dark:hover:bg-white/10"
        >
          <span aria-hidden="true">←</span>
          <span className="lg:sr-only">Back to list</span>
        </button>

        {data ? (
          <>
            <TypeIcon type={vocab.type(data.type)} />
            <span className="font-mono text-sm font-semibold text-[color:var(--color-accent)]">
              {data.key}
            </span>
          </>
        ) : (
          <SkeletonBar className="h-4 w-24" />
        )}

        <div className="ml-auto flex items-center gap-2">
          {ticket.isRefreshing ? <Spinner label="Refreshing ticket" /> : null}
          {/* 🔴 PH-57's edit half. Until this button existed the only ticket PATCH this app made was
              `/rank` — so a title typo, a wrong assignee or a missing estimate could be READ here
              and changed nowhere. ⛔ Deliberately NOT admin-gated: the API gates on active project
              membership, and a stricter UI removes the feature from people entitled to it. */}
          {data ? (
            <button
              type="button"
              onClick={() => editor.edit(data)}
              className="h-9 rounded border border-[color:var(--color-line)] px-2.5 text-sm font-medium"
            >
              Edit
            </button>
          ) : null}
          {data ? (
            <StatusDropdown
              ticketId={data.id}
              statusCode={data.status}
              compact
              onChanged={() => {
                ticket.reload();
                onStatusChanged();
              }}
            />
          ) : null}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4">
        {ticket.isStale && ticket.error ? (
          <div className="mb-3">
            <StaleBanner error={ticket.error} loadedAt={ticket.loadedAt} onRetry={ticket.reload} />
          </div>
        ) : null}

        {ticket.error && !ticket.isStale ? (
          <ErrorNotice error={ticket.error} onRetry={ticket.reload} />
        ) : null}

        {ticket.isLoading ? (
          <div className="space-y-3" role="status" aria-label="Loading ticket">
            <SkeletonBar className="h-6 w-3/4" />
            <SkeletonBar className="h-24 w-full" />
            <SkeletonBar className="h-40 w-full" />
          </div>
        ) : null}

        {data ? (
          <div className={`space-y-6 ${ticket.isStale ? 'opacity-60' : ''}`}>
            <h2 className="text-lg leading-snug font-semibold">{data.title}</h2>

            <section aria-labelledby="description-heading">
              <h3 id="description-heading" className="mb-1.5 text-sm font-semibold">
                Description
              </h3>
              {/* Plain text, whitespace preserved. The [Story]/[Expectation]/[Details]/[Document]
                  convention reads fine as text and needs no markup — see CommentList for why. */}
              {data.description ? (
                <p className="text-sm leading-relaxed whitespace-pre-wrap text-[color:var(--color-ink)]">
                  {data.description}
                </p>
              ) : (
                <p className="text-sm text-[color:var(--color-ink-muted)]">No description.</p>
              )}
            </section>

            <section aria-labelledby="details-heading">
              <h3 id="details-heading" className="mb-2 text-sm font-semibold">
                Details
              </h3>
              <DetailFields ticket={data} onOpenTicket={onOpenTicket} />
              <GitlabCodePanel
                ticketId={data.id}
                projectGitlabId={projectGitlabId ?? null}
                issueIid={data.gitlab_issue_iid}
              />
            </section>

            {data.links.length > 0 ? (
              <section aria-labelledby="links-heading">
                <h3 id="links-heading" className="mb-2 text-sm font-semibold">
                  Links
                </h3>
                <LinkList links={data.links} />
              </section>
            ) : null}

            {/* ⚠️ NO MERGE-REQUEST SECTION. `ticket_merge_request` exists in db/001, but the API
                exposes no endpoint for it yet, so there is nothing to read. ⛔ Not stubbed with an
                empty list — that would read as "this ticket has no MRs", which is a different
                claim from "we cannot see them". */}

            {data.subtasks.length > 0 ? (
              <SubtaskTable subtasks={data.subtasks} onOpen={(task) => onOpenTicket(task.id)} />
            ) : null}

            <section aria-labelledby="activity-heading">
              <h3 id="activity-heading" className="sr-only">
                Activity
              </h3>
              <div role="tablist" aria-label="Activity" className="mb-3 flex gap-1">
                <TabButton active={tab === 'comments'} onClick={() => setTab('comments')}>
                  Comments
                </TabButton>
                <TabButton active={tab === 'history'} onClick={() => setTab('history')}>
                  Status history
                </TabButton>
              </div>

              {tab === 'comments' ? (
                <CommentList
                  ticketId={data.id}
                  comments={data.comments}
                  onAdded={() => ticket.reload()}
                />
              ) : (
                <StatusHistory entries={data.status_history} />
              )}
            </section>

            <p className="border-t border-[color:var(--color-line)] pt-3 text-xs text-[color:var(--color-ink-subtle)]">
              Created {formatDateTime(data.created_at)} · Updated {formatDateTime(data.updated_at)}
            </p>
          </div>
        ) : null}
      </div>

      {editor.open ? (
        <TicketFormDialog
          mode={editor.open}
          sprints={sprintsResource.data ?? NO_SPRINTS}
          versions={versions}
          busy={editor.busy}
          error={editor.error}
          problem={editor.problem}
          onCancel={editor.cancel}
          onSubmit={editor.submit}
        />
      ) : null}
    </aside>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`h-8 rounded px-2.5 text-sm font-medium ${
        active
          ? 'bg-[color:var(--color-surface-sunken)] text-[color:var(--color-ink)]'
          : 'text-[color:var(--color-ink-muted)] hover:bg-black/5 dark:hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  );
}
